import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { useState, useEffect } from "react"

const occasions = [
  {
    id: "voyage",
    title: "Album de Voyage",
    description: "Immortalisez vos aventures\net découvertes",
    albums: ["/images/occasions/voyage1.jpg", "/images/occasions/voyage2.jpg", "/images/occasions/voyage3.jpg"],
    color: "from-blue-500/20 to-cyan-500/20",
    icon: "",
    link: "/album-photo-voyage"
  },
  {
    id: "mariage", 
    title: "Album de Mariage",
    description: "Conservez la magie de\nvos moments précieux",
    albums: ["/images/occasions/mariage1.jpg", "/images/occasions/mariage2.jpg", "/images/occasions/mariage3.jpg"],
    color: "from-pink-500/20 to-rose-500/20",
    icon: "",
    link: "/album-photo-mariage"
  },
  {
    id: "famille",
    title: "Album de Famille", 
    description: "Rassemblez tous\nvos souvenirs familiaux",
    albums: ["/images/occasions/famille1.jpg", "/images/occasions/famille2.jpg", "/images/occasions/famille3.jpg"],
    color: "from-green-500/20 to-emerald-500/20",
    icon: "",
    link: "/album-photo-famille"
  },
  {
    id: "naissance",
    title: "Album de Naissance",
    description: "Capturez chaque étape\nde leur croissance", 
    albums: ["/images/occasions/naissance1.jpg", "/images/occasions/naissance2.jpg", "/images/occasions/naissance3.jpg"],
    color: "from-[#f9bb72]/20 to-[#f9bb72]/30",
    icon: "",
    link: "/album-photo-naissance"
  }
]

interface CarouselProps {
  albums: string[]
  title: string
}

function Carousel({ albums, title }: CarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % albums.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [albums.length])

  return (
    <div className="relative w-full h-48 overflow-hidden rounded-xl">
      {albums.map((album, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-500 ${
            index === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img 
            src={album} 
            alt={`${title} ${index + 1}`}
            className="w-full h-full object-cover"
          />
        </div>
      ))}
      
      {/* Indicators */}
      <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {albums.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex 
                ? 'bg-white shadow-lg' 
                : 'bg-white/50 hover:bg-white/80'
            }`}
          />
        ))}
      </div>
    </div>
  )
}

export default function OccasionsSection() {
  return (
    <section id="occasions" className="py-12 bg-gradient-to-b from-background to-soft-cream/30">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground">
            Pour quelle <span className="text-gradient">occasion ?</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Chaque moment de votre vie mérite un album unique. Découvrez nos collections spécialement conçues pour vos événements.
          </p>
        </div>

        {/* Occasions Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {occasions.map((occasion) => (
            <Card 
              key={occasion.id}
              className="group overflow-hidden border-0 shadow-card hover:shadow-elegant hover:scale-105 transition-all duration-500 bg-card/80 backdrop-blur-sm"
            >
              <CardContent className="p-0">
                <Carousel albums={occasion.albums} title={occasion.title} />
                
                <div className="p-6 space-y-4 text-center">
                  <div className="flex items-center justify-center space-x-3">
                    <span className="text-2xl">{occasion.icon}</span>
                    <h3 className="text-xl font-bold text-foreground">{occasion.title}</h3>
                  </div>
                  
                  <p className="text-muted-foreground text-sm whitespace-pre-line">
                    {occasion.description}
                  </p>
                  
                  <Button variant="rainbow" className="w-full" asChild>
                    <Link to={occasion.link}>
                      Découvrir
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  )
}